import { Component, computed, input, output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TextInputComponent } from '@shared/ui/components/form/text-input/text-input.component';
import { ButtonComponent } from '@shared/ui/components/button/button.component';
import { MatRadioModule } from '@angular/material/radio';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { LoginCredentials } from '@core/auth';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

/**
 * Sign-In Form Component
 *
 * Pure presentational component for email input and submission.
 * No business logic or service injection.
 */
@Component({
  selector: 'app-sign-in-password-form',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TextInputComponent,
    ButtonComponent,
    MatRadioModule,
    MatInputModule,
    MatFormFieldModule,
  ],
  template: `
    <form [formGroup]="formGroup" class="sign-in-form" (ngSubmit)="onSubmit()">
      <app-text-input
        [control]="$any(formGroup.get('email'))"
        formControlName="email"
        type="email"
        label="Email"
        placeholder="Email"
        name="email"
        autocomplete="email"
      />
      @if (showPasswordField()) {
        <app-text-input
          [control]="$any(formGroup.get('password'))"
          formControlName="password"
          type="password"
          label="Password"
          placeholder="Password"
          name="password"
        />
      }

      <div class="buttons">
        <app-button
          type="submit"
          variant="secondary"
          [loading]="isSubmitting()"
          [disabled]="isButtonDisabled()"
        >
          Login
        </app-button>
        <app-button type="button" variant="secondary" (click)="goBack.emit()"> Go back </app-button>
      </div>
    </form>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
      }
      .sign-in-form {
        display: flex;
        flex-direction: column;
        gap: var(--aida-ref-spacing-125);
      }

      .buttons {
        display: flex;
        flex-direction: column;
        gap: var(--aida-ref-spacing-125);
      }
    `,
  ],
})
export class SignInFormComponent {
  isSubmitting = input<boolean>(false);
  showPasswordField = input<boolean>(true);
  formSubmit = output<LoginCredentials>();
  onFieldChange = output<void>();
  goBack = output<void>();

  constructor() {
    const emailText = this.formGroup.get('email');
    const passwordText = this.formGroup.get('password');

    emailText?.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.fieldChange();
    });

    passwordText?.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.fieldChange();
    });
  }

  formGroup = new FormGroup({
    email: new FormControl('', [Validators.email]),
    password: new FormControl({ value: '', disabled: false }),
  });

  private formSignal = toSignal(this.formGroup.valueChanges, {
    initialValue: this.formGroup.getRawValue(),
  });

  private formStatus = toSignal(this.formGroup.statusChanges, {
    initialValue: this.formGroup.status,
  });

  isButtonDisabled = computed(() => {
    if (this.isSubmitting()) return true;
    if (this.formStatus() === 'INVALID') return true;

    const form = this.formSignal();
    const email = form.email?.trim();
    const password = form.password;

    return !email || (this.showPasswordField() && !password);
  });

  protected onSubmit(): void {
    this.formGroup.markAllAsTouched();
    const email = this.formGroup.controls.email?.value;
    const password = this.formGroup.controls.password.value;

    if (this.isSubmitting() || !email || (this.showPasswordField() && !password)) return;

    this.formSubmit.emit({ username: email, password: password });
  }

  protected fieldChange(): void {
    this.onFieldChange.emit();
  }
}
